import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, GitCompareArrows, Check, Lock, TrendingUp, TrendingDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { getLocationLabel } from '../lib/location-utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = ['#005F73', '#0A9396', '#EE9B00', '#BB3E03'];

export default function Compare() {
  const navigate = useNavigate();
  const { getAuthHeader, user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [selected, setSelected] = useState([]);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  const canCompare = user && ['professional', 'enterprise'].includes(user.plan);

  useEffect(() => {
    if (!canCompare) return;
    const fetchProjects = async () => {
      try {
        const res = await axios.get(`${API}/projects`, { headers: getAuthHeader() });
        setProjects(res.data);
      } catch (e) {
        console.error(e);
      }
    };
    fetchProjects();
  }, []);

  const toggleSelect = (id) => {
    setSelected(prev =>
      prev.includes(id)
        ? prev.filter(p => p !== id)
        : prev.length < 4 ? [...prev, id] : prev
    );
    setResults(null);
  };

  const handleCompare = async () => {
    if (selected.length < 2) {
      toast.error('Sélectionnez au moins 2 projets');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/projects/compare`, selected, {
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' }
      });
      setResults(res.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Fonctionnalité réservée au plan Professional');
        navigate('/pricing');
      } else {
        toast.error('Erreur lors de la comparaison');
      }
    } finally {
      setLoading(false);
    }
  };

  const fmt = (n) => {
    const parts = Number(n).toFixed(2).split('.');
    const intPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    const dec = parts[1] === '00' ? '' : ',' + parts[1];
    return intPart + dec;
  };

  if (!canCompare) {
    return (
      <div className="min-h-screen bg-warm-sand flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl border border-stone-100 p-8 sm:p-12 text-center max-w-md shadow-lg">
          <Lock className="w-12 h-12 text-stone-300 mx-auto mb-4" />
          <h2 className="text-2xl font-semibold text-[#005F73] mb-3">Comparaison de projets</h2>
          <p className="text-stone-600 mb-6">
            Cette fonctionnalité est disponible à partir du plan <strong>Professional</strong>.
          </p>
          <Button
            onClick={() => navigate('/pricing')}
            className="rounded-full px-8 bg-[#EE9B00] hover:bg-[#EE9B00]/90 text-white"
            data-testid="btn-upgrade"
          >
            Voir les plans
          </Button>
        </div>
      </div>
    );
  }

  const chartData = results ? results.map((r, i) => ({
    name: r.project.name.length > 15 ? r.project.name.substring(0, 15) + '...' : r.project.name,
    'Rend. brut (%)': r.results.gross_yield,
    'Rend. net (%)': r.results.net_yield,
    'ROI (%)': r.results.roi,
  })) : [];

  const cashflowData = results ? results.map((r, i) => ({
    name: r.project.name.length > 15 ? r.project.name.substring(0, 15) + '...' : r.project.name,
    'Cash-flow mensuel': r.results.monthly_cashflow,
  })) : [];

  return (
    <div className="min-h-screen bg-warm-sand">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-semibold text-[#005F73] mb-2 tracking-tight">
              Comparaison
            </h1>
            <p className="text-base sm:text-lg text-stone-600">
              Sélectionnez 2 à 4 projets à comparer
            </p>
          </div>
          <Button
            onClick={handleCompare}
            disabled={selected.length < 2 || loading}
            className="rounded-full px-6 py-5 bg-[#005F73] hover:bg-[#005F73]/90 text-white shadow-lg disabled:bg-stone-300 w-full sm:w-auto"
            data-testid="btn-compare"
          >
            <GitCompareArrows className="w-5 h-5 mr-2" />
            {loading ? 'Analyse...' : `Comparer (${selected.length})`}
          </Button>
        </div>

        {/* Project selection */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
          {projects.map(project => {
            const isSelected = selected.includes(project.id);
            return (
              <div
                key={project.id}
                onClick={() => toggleSelect(project.id)}
                data-testid={`compare-project-${project.id}`}
                className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                  isSelected
                    ? 'border-[#005F73] bg-[#005F73]/5 shadow-md'
                    : 'border-stone-200 bg-white hover:border-stone-300'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-semibold text-[#005F73]">{project.name}</h3>
                    <p className="text-sm text-stone-500">{project.property_type} - {getLocationLabel(project.location, project.department)}</p>
                    <p className="text-sm text-stone-600 mt-1">
                      {fmt(project.purchase_price)} EUR | {fmt(project.monthly_rent)} EUR/mois
                    </p>
                  </div>
                  {isSelected && (
                    <div className="w-6 h-6 rounded-full bg-[#005F73] flex items-center justify-center shrink-0">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {projects.length === 0 && (
          <div className="text-center py-16">
            <p className="text-stone-500 mb-4">Aucun projet à comparer. Créez d'abord des projets.</p>
            <Button onClick={() => navigate('/calculator')} className="rounded-full px-6 bg-[#EE9B00] hover:bg-[#EE9B00]/90 text-white">
              Créer un projet
            </Button>
          </div>
        )}

        {/* Results */}
        {results && (
          <div className="space-y-8" data-testid="compare-results">
            {/* Comparison table */}
            <div className="bg-white rounded-2xl border border-stone-100 overflow-x-auto shadow-sm">
              <table className="w-full text-sm" data-testid="compare-table">
                <thead>
                  <tr className="border-b border-stone-100">
                    <th className="text-left p-4 text-stone-500 font-medium min-w-[140px]">Indicateur</th>
                    {results.map((r, i) => (
                      <th key={r.project.id} className="p-4 text-center font-semibold min-w-[140px]" style={{ color: COLORS[i] }}>
                        {r.project.name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[
                    { label: 'Type', key: 'property_type', isProject: true },
                    { label: 'Localisation', key: 'location', isProject: true },
                    { label: "Prix d'achat", key: 'purchase_price', isProject: true, suffix: ' EUR', format: true },
                    { label: 'Loyer mensuel', key: 'monthly_rent', isProject: true, suffix: ' EUR', format: true },
                    { label: 'Invest. total', key: 'total_investment', suffix: ' EUR', format: true },
                    { label: 'Rend. brut', key: 'gross_yield', suffix: ' %', highlight: true },
                    { label: 'Rend. net', key: 'net_yield', suffix: ' %', highlight: true },
                    { label: 'Cash-flow/mois', key: 'monthly_cashflow', suffix: ' EUR', format: true, highlight: true },
                    { label: 'Cash-flow/an', key: 'annual_cashflow', suffix: ' EUR', format: true },
                    { label: 'ROI', key: 'roi', suffix: ' %', highlight: true },
                    { label: 'Mensualité prêt', key: 'monthly_loan_payment', suffix: ' EUR', format: true },
                    { label: 'Charges/an', key: 'annual_charges', suffix: ' EUR', format: true },
                    { label: 'Réduction fiscale', key: 'tax_reduction', suffix: ' EUR', format: true },
                  ].map((row) => {
                    const values = results.map(r => row.isProject ? r.project[row.key] : r.results[row.key]);
                    const numValues = values.filter(v => typeof v === 'number');
                    const best = row.highlight && numValues.length > 0 ? Math.max(...numValues) : null;

                    return (
                      <tr key={row.key} className="border-b border-stone-50 hover:bg-stone-50/50">
                        <td className="p-4 text-stone-600 font-medium">{row.label}</td>
                        {results.map((r, i) => {
                          const rawVal = row.isProject ? r.project[row.key] : r.results[row.key];
                          const val = row.key === 'location' ? getLocationLabel(r.project.location, r.project.department) : rawVal;
                          const isBest = row.highlight && rawVal === best && numValues.filter(v => v === best).length === 1;
                          const displayVal = row.format && typeof rawVal === 'number' ? fmt(rawVal) : val;

                          return (
                            <td key={r.project.id} className={`p-4 text-center ${isBest ? 'font-bold' : ''}`}>
                              <span className={isBest ? 'text-[#0A9396]' : 'text-stone-700'}>
                                {displayVal}{typeof rawVal === 'number' ? (row.suffix || '') : ''}
                              </span>
                              {isBest && <TrendingUp className="w-3 h-3 inline ml-1 text-[#0A9396]" />}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-2xl border border-stone-100 p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-[#005F73] mb-4">Rendements comparés (%)</h3>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={chartData}>
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="Rend. brut (%)" fill="#005F73" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="Rend. net (%)" fill="#0A9396" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="ROI (%)" fill="#EE9B00" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="bg-white rounded-2xl border border-stone-100 p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-[#005F73] mb-4">Cash-flow mensuel (EUR)</h3>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={cashflowData}>
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip />
                    <Bar dataKey="Cash-flow mensuel" radius={[4, 4, 0, 0]}>
                      {cashflowData.map((entry, i) => (
                        <rect key={i} fill={entry['Cash-flow mensuel'] >= 0 ? '#0A9396' : '#BB3E03'} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
